import { useState, useEffect, useMemo } from 'react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { Card } from '@/components/ui/card'
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react'
import { integrationService, type HistoricalPerformanceReview } from '@/services/api/integrationService'
import { reviewService } from '@/services/api/reviewService'

interface PerformanceHistoryTrendProps {
  userId: string
  employeeId?: string
  currentYear: number
}

interface YearRating {
  year: number
  period: 'mid-year' | 'year-end'
  rating_id: string
  rating_description: string
  source: 'F&O' | 'Platform'
  review_type?: string
}

interface ChartDataPoint {
  period: string
  shortPeriod: string
  rating: number
  ratingId: string
  ratingLabel: string
  year: number
  reviewType: string
  source: string
  color: string
}

// Rating configuration with numeric values for charting
const ratingConfig: Record<string, { 
  label: string
  shortLabel: string
  value: number
  color: string
}> = {
  'EP': { label: 'Exceptional', shortLabel: 'Exceptional', value: 4, color: '#10b981' },
  'FP': { label: 'Full', shortLabel: 'Full', value: 3, color: '#3b82f6' },
  'NY': { label: 'Not Yet Full', shortLabel: 'Developing', value: 2, color: '#f59e0b' },
  'WP': { label: 'Weak', shortLabel: 'Needs Improvement', value: 1, color: '#ef4444' },
}

const getConfig = (ratingId: string) => {
  return ratingConfig[ratingId?.toUpperCase()] || ratingConfig['FP']
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  
  const data = payload[0].payload as ChartDataPoint
  const config = getConfig(data.ratingId)
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <div className="font-semibold text-gray-900 mb-2">{data.period}</div>
      <div className="flex items-center gap-2 mb-1">
        <span 
          className="w-3 h-3 rounded-full" 
          style={{ backgroundColor: config.color }}
        />
        <span className="font-bold text-lg" style={{ color: config.color }}>
          {data.ratingId}
        </span>
        <span className="text-gray-600 text-sm">({config.shortLabel})</span>
      </div>
      <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
        <span className={`px-1.5 py-0.5 rounded ${data.source === 'F&O' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
          {data.source === 'F&O' ? 'Legacy System' : 'Current Platform'}
        </span>
      </div>
    </div>
  )
}

// Custom dot component for the chart
const CustomDot = (props: any) => {
  const { cx, cy, payload } = props
  if (!payload) return null
  
  const config = getConfig(payload.ratingId)
  
  return (
    <g>
      <circle cx={cx} cy={cy} r={8} fill={config.color} stroke="white" strokeWidth={3} />
      <circle cx={cx} cy={cy} r={4} fill="white" />
    </g>
  )
}

export function PerformanceHistoryTrend({ userId, employeeId, currentYear }: PerformanceHistoryTrendProps) {
  const [loading, setLoading] = useState(true)
  const [ratings, setRatings] = useState<YearRating[]>([])

  useEffect(() => {
    loadPerformanceHistory()
  }, [userId, employeeId, currentYear])

  const SYSTEM_LAUNCH_YEAR = 2026

  const loadPlatformReviews = async (year: number): Promise<YearRating[]> => {
    try {
      const reviews = await reviewService.getUserReviewCycle(userId, year)
      const ratings: YearRating[] = []
      reviews.forEach((review: any) => {
        // Prefer the calibrated rating (post-calibration) over the initial. The
        // cycle endpoint returns calibrated_rating + initial_rating, NOT a single
        // `overall_rating` — so reading review.overall_rating silently dropped
        // every completed review and never surfaced the calibrated value.
        const ratingVal = review.calibrated_rating || review.initial_rating
        if (review.status === 'completed' && ratingVal) {
          let ratingId = 'FP'
          let ratingDesc = ratingVal

          // Order matters: 'not-yet-full' contains 'full', so the not-yet
          // checks MUST run before the plain 'full' check (the old order
          // classified every Not Yet Full rating as Full). TSTR first — it
          // never collides but is the most specific token.
          const ratingLower = ratingVal?.toLowerCase() ?? ''
          if (ratingLower.includes('tstr') || ratingLower.includes('too soon')) {
            ratingId = 'TS'
            ratingDesc = 'Too Soon to Rate'
          } else if (ratingLower.includes('not yet') || ratingLower.includes('not-yet') || ratingLower.includes('not_yet') || ratingLower.includes('developing')) {
            ratingId = 'NY'
            ratingDesc = 'Not Yet Full'
          } else if (ratingLower.includes('exceptional')) {
            ratingId = 'EP'
            ratingDesc = 'Exceptional'
          } else if (ratingLower.includes('weak') || ratingLower.includes('below')) {
            ratingId = 'WP'
            ratingDesc = 'Weak'
          } else if (ratingLower.includes('full')) {
            ratingId = 'FP'
            ratingDesc = 'Full'
          }

          ratings.push({
            year,
            period: review.review_type === 'midyear' ? 'mid-year' : 'year-end',
            rating_id: ratingId,
            rating_description: ratingDesc,
            source: 'Platform',
            review_type: review.review_type
          })
        }
      })
      return ratings
    } catch {
      return []
    }
  }

  const loadPerformanceHistory = async () => {
    setLoading(true)
    const allRatings: YearRating[] = []
    const priorYears = [currentYear - 2, currentYear - 1]

    try {
      // Load prior years — from F&O if pre-launch, from platform if post-launch
      for (const year of priorYears) {
        if (year < SYSTEM_LAUNCH_YEAR && employeeId) {
          // Legacy year: fetch from F&O
          const historicalData = await integrationService.getHistoricalReviews(employeeId, year)
          historicalData.reviews.forEach((review: HistoricalPerformanceReview) => {
            if (review.final_rating_id) {
              allRatings.push({
                year,
                period: review.review_type === 'year-end' ? 'year-end' : 'mid-year',
                rating_id: review.final_rating_id,
                rating_description: review.final_rating_description || review.final_rating_id,
                source: 'F&O',
                review_type: review.review_type
              })
            }
          })
        } else {
          // Platform year: fetch from system DB
          const ratings = await loadPlatformReviews(year)
          allRatings.push(...ratings)
        }
      }

      // Load selected year from platform
      const currentYearRatings = await loadPlatformReviews(currentYear)
      allRatings.push(...currentYearRatings)

      setRatings(allRatings)
    } catch (error) {
      console.error('Failed to load performance history:', error)
    } finally {
      setLoading(false)
    }
  }

  // Transform ratings to chart data points, sorted chronologically
  const chartData = useMemo((): ChartDataPoint[] => {
    return ratings
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year
        // Mid-year comes before year-end
        return a.period === 'mid-year' ? -1 : 1
      })
      .map(r => {
        const config = getConfig(r.rating_id)
        const periodLabel = r.period === 'mid-year' ? 'Mid-Year' : 'Year-End'
        return {
          period: `${periodLabel} ${r.year}`,
          shortPeriod: `${r.period === 'mid-year' ? 'H1' : 'H2'} '${String(r.year).slice(-2)}`,
          rating: config.value,
          ratingId: r.rating_id,
          ratingLabel: config.shortLabel,
          year: r.year,
          reviewType: r.period,
          source: r.source,
          color: config.color
        }
      })
  }, [ratings])

  // Calculate trend
  const trendInfo = useMemo(() => {
    if (chartData.length < 2) return { direction: 'stable' as const, change: 0 }
    const first = chartData[0].rating
    const last = chartData[chartData.length - 1].rating
    const change = last - first
    return {
      direction: change > 0 ? 'up' as const : change < 0 ? 'down' as const : 'stable' as const,
      change: Math.abs(change)
    }
  }, [chartData])

  // Get latest rating
  const latestRating = chartData.length > 0 ? chartData[chartData.length - 1] : null

  if (loading) {
    return (
      <Card className="p-5 mb-4 border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-heritage-blue mr-2" />
          <span className="text-sm text-gray-600">Loading impact & growth history...</span>
        </div>
      </Card>
    )
  }

  if (ratings.length === 0) {
    return null
  }

  return (
    <Card className="mb-4 overflow-hidden border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-heritage-blue to-future-blue p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Impact & Growth Journey</h3>
              <p className="text-blue-100 text-sm">Impact indicator trend across cycles</p>
            </div>
          </div>
          
          {/* Current Rating & Trend */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-blue-100 mb-1">Trend</div>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                trendInfo.direction === 'up' ? 'bg-emerald-500/20 text-emerald-100' :
                trendInfo.direction === 'down' ? 'bg-red-500/20 text-red-100' :
                'bg-white/20 text-white'
              }`}>
                {trendInfo.direction === 'up' && <TrendingUp className="h-4 w-4" />}
                {trendInfo.direction === 'down' && <TrendingDown className="h-4 w-4" />}
                {trendInfo.direction === 'stable' && <Minus className="h-4 w-4" />}
                {trendInfo.direction === 'up' ? 'Growing' : trendInfo.direction === 'down' ? 'Declining' : 'Stable'}
              </div>
            </div>
            
            {latestRating && (
              <div className="text-right border-l border-white/20 pl-4">
                <div className="text-xs text-blue-100 mb-1">Latest</div>
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold"
                  style={{ backgroundColor: `${latestRating.color}30` }}
                >
                  <span>{latestRating.ratingId}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-5 bg-white">
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ratingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis 
                dataKey="shortPeriod" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6b7280', fontSize: 12 }}
                dy={10}
              />
              <YAxis 
                domain={[0, 5]}
                ticks={[1, 2, 3, 4]}
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickFormatter={(value) => {
                  const labels: Record<number, string> = { 1: 'WP', 2: 'NY', 3: 'FP', 4: 'EP' }
                  return labels[value] || ''
                }}
                width={35}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Reference lines for rating levels */}
              <ReferenceLine y={4} stroke="#10b981" strokeDasharray="3 3" strokeOpacity={0.5} />
              <ReferenceLine y={3} stroke="#3b82f6" strokeDasharray="3 3" strokeOpacity={0.5} />
              <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.5} />
              
              <Area
                type="monotone"
                dataKey="rating"
                stroke="#3b82f6"
                strokeWidth={3}
                fill="url(#ratingGradient)"
                dot={<CustomDot />}
                activeDot={{ r: 10, fill: '#3b82f6', stroke: 'white', strokeWidth: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
          {Object.entries(ratingConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: config.color }}
              />
              <span className="text-gray-600 font-medium">{key}</span>
              <span className="text-gray-400">= {config.shortLabel}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}
